import { useAuth0 } from '@auth0/auth0-react';
import {
  AntdLayout,
  Avatar,
  Dropdown,
  Icons,
  Menu,
  Select,
  Space,
  Typography,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { useUserData } from 'hooks';
import { useEffect, useState } from 'react';
import { getAppInstances } from 'services/app-instance';
import { getOrganizations } from 'services/organization';
import useAppInstanceStore from 'store/useAppInstanceStore';
import useOrganizationStore from 'store/useOrganizationStore';

const { Header: AntdHeader } = AntdLayout;
const { DownOutlined } = Icons;
const { Text } = Typography;
const { Option } = Select;

export const Header: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { appInstances, setAppInstances, currentAppInstance, setCurrentAppInstance } =
    useAppInstanceStore();
  const { organizations, currentOrganization, setOrganizations, setCurrentOrganization } =
    useOrganizationStore();

  const { user, loginWithRedirect, logout } = useAuth0();
  const userData = useUserData();
  const { push } = useNavigation();

  const fetchOrgAndAppInstance = async () => {
    setIsLoading(true);
    try {
      const [organizationResponse, appInstanceResponse] = await Promise.all([
        getOrganizations(),
        getAppInstances(),
      ]);
      setOrganizations(organizationResponse);
      setAppInstances(appInstanceResponse);
    } catch (err: any) {
      console.error(err?.message ?? err ?? 'Failed to fetch org and app instances');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    fetchOrgAndAppInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (userData.user) {
      const tokenOrgId = userData.user.organization.id;
      const tokenAppId = userData.user.platform.id;

      const currentOrganization = organizations.find((org) => org.id === tokenOrgId);
      if (currentOrganization) {
        setCurrentOrganization(currentOrganization);
      }

      const currentAppInstance = appInstances.find((app) => app.id === tokenAppId);
      if (currentAppInstance) {
        setCurrentAppInstance(currentAppInstance);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations, appInstances, userData.user]);

  const onChangeOrgOption = (id: string) => {
    if (currentOrganization && id === currentOrganization.id) return;

    const redirectUri = `${window.location.origin}/apps`;
    const selectedOrganization = organizations.find((org) => org.id === id);
    if (selectedOrganization) {
      window.localStorage.clear();
      loginWithRedirect({ orgId: id, redirectUri });
      setCurrentOrganization(selectedOrganization);
    }
  };

  const onChangeAppOption = (id: string) => {
    if (currentAppInstance && id === currentAppInstance.id) return;

    const selectedAppInstance = appInstances.find((app) => app.id === id);
    if (currentOrganization && selectedAppInstance) {
      window.localStorage.clear();
      loginWithRedirect({ orgId: currentOrganization.id, platformId: selectedAppInstance.id });
      setCurrentAppInstance(selectedAppInstance);
    }
  };

  const menu = (
    <Menu mode="vertical" selectedKeys={[]}>
      <Menu.Item
        key={'orgs'}
        icon={<Icons.BankOutlined />}
        onClick={() => {
          push('/organizations');
        }}
      >
        My Orgs
      </Menu.Item>
      <Menu.Item
        key={'logout'}
        icon={<Icons.LogoutOutlined />}
        onClick={() => {
          localStorage.clear();
          logout({ returnTo: window.location.origin });
        }}
      >
        Logout
      </Menu.Item>
    </Menu>
  );

  return (
    <AntdHeader
      style={{
        background: 'white',
        padding: '0 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Select
          loading={isLoading}
          suffixIcon={<Icons.BankOutlined />}
          placeholder="Select Org"
          value={currentOrganization?.id}
          style={{ minWidth: '200px' }}
          onChange={(id: string) => onChangeOrgOption(id)}
          disabled={isLoading}
        >
          {organizations.map((org) => (
            <Option key={org.id} value={org.id}>
              {org.displayName}
            </Option>
          ))}
        </Select>

        <Select
          loading={isLoading}
          suffixIcon={<Icons.FolderOutlined />}
          placeholder="Select App"
          value={currentAppInstance?.id}
          style={{ minWidth: '200px' }}
          onChange={(id: string) => onChangeAppOption(id)}
          disabled={isLoading}
        >
          {appInstances.map((app) => (
            <Option key={app.id} value={app.id}>
              {app.displayName}
            </Option>
          ))}
        </Select>

        <Icons.SettingOutlined
          onClick={() => push('apps')}
          style={{
            cursor: 'pointer',
            fontSize: '1rem',
          }}
        />
      </div>

      <div style={{ marginLeft: 'auto' }}>
        <Dropdown overlay={menu}>
          <button
            style={{
              color: 'inherit',
              background: 'inherit',
              padding: 0,
              border: 'none',
            }}
          >
            <Space>
              <Text ellipsis strong>
                {userData.user?.name}
              </Text>
              <DownOutlined style={{ fontSize: '12px' }} />
              <Avatar size="large" src={userData.user?.picture} alt={userData.user?.name} />
            </Space>
          </button>
        </Dropdown>
      </div>
    </AntdHeader>
  );
};
